import React, { useState } from "react"
import jsPDF from "jspdf"
import QRCode from "qrcode"
import { FiDownload, FiExternalLink, FiLoader } from "react-icons/fi"

// ── A4 landscape layout constants (mm) ──────────────────────────────────────
const LX = 22   // left margin for content
const RX = 155  // right boundary (before wave decoration)

// RGB helpers
const C = {
  dark:   [15,  23,  42],   // #0F172A
  mid:    [100, 116, 139],  // #64748B
  light:  [148, 163, 184],  // #94A3B8
  teal:   [0,   188, 140],  // ~caribbeangreen
  purple: [99,  102, 241],  // #6366F1
  white:  [255, 255, 255],
}

function ScoreCol({ label, score }) {
  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <p className="text-xs text-richblack-400">{label}</p>
      <p className="text-2xl font-bold text-richblack-5">{score ?? "—"}%</p>
      <div className="h-1 w-full overflow-hidden rounded-full bg-richblack-600">
        <div
          className="h-full rounded-full bg-caribbeangreen-300 transition-all duration-700"
          style={{ width: `${score ?? 0}%` }}
        />
      </div>
    </div>
  )
}

export default function CertificateCard({ certificate, studentName }) {
  const [downloading, setDownloading] = useState(false)

  if (!certificate) return null

  const cert        = certificate
  const courseName  = cert.course?.courseName ?? "Course"
  const skillTags   = cert.skillTags ?? cert.course?.tag ?? []
  const issuedAt    = cert.issuedAt ? new Date(cert.issuedAt) : new Date()
  const validUntil  = cert.validUntil
    ? new Date(cert.validUntil)
    : new Date(issuedAt.getTime() + 2 * 365 * 24 * 60 * 60 * 1000)

  const issueLabel  = issuedAt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
  const issueYear   = issuedAt.getFullYear()
  const issueMonth  = issuedAt.getMonth() + 1
  const validYear   = validUntil.getFullYear()
  const validMonth  = validUntil.getMonth() + 1

  const credUrl     = `https://www.kodevedic.com/certificate/${cert.credentialId}`

  const certNameWithSkills = skillTags.length > 0
    ? `${courseName} · ${skillTags.slice(0, 5).join(", ")}`
    : courseName

  const linkedInUrl = [
    "https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME",
    `name=${encodeURIComponent(certNameWithSkills)}`,
    `organizationId=`,
    `organizationName=${encodeURIComponent("kodevedic-edtech")}`,
    `issueYear=${issueYear}`,
    `issueMonth=${issueMonth}`,
    `expirationYear=${validYear}`,
    `expirationMonth=${validMonth}`,
    `certUrl=${encodeURIComponent(credUrl)}`,
    `certId=${encodeURIComponent(cert.credentialId)}`,
  ].join("&")

  // ── jsPDF generation ──────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    setDownloading(true)
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" })

      // ── Load Garet font from public/fonts/Garet-Heavy.ttf ─────────────────
      let hasGaret = false
      try {
        const fr = await fetch("/fonts/Garet-Heavy.ttf")
        if (fr.ok) {
          const bytes = new Uint8Array(await fr.arrayBuffer())
          const b64   = btoa(bytes.reduce((s, b) => s + String.fromCharCode(b), ""))
          doc.addFileToVFS("Garet-Heavy.ttf", b64)
          doc.addFont("Garet-Heavy.ttf", "Garet", "normal")
          hasGaret = true
        }
      } catch { /* fallback to helvetica */ }

      // ── Helpers ────────────────────────────────────────────────────────────
      const toB64 = (blob) => new Promise((res) => {
        const r = new FileReader()
        r.onloadend = () => res(r.result)
        r.readAsDataURL(blob)
      })

      // f(family, style, size, color)
      const f = (family, style, size, color) => {
        doc.setFont(family, style)
        doc.setFontSize(size)
        doc.setTextColor(...color)
      }

      // ── 1. Background ──────────────────────────────────────────────────────
      try {
        doc.addImage(await toB64(await (await fetch("/cer-1.jpg")).blob()), "JPEG", 0, 0, 297, 210)
      } catch {
        doc.setFillColor(255, 255, 255)
        doc.rect(0, 0, 297, 210, "F")
      }

      // ── 2. "Certificate of Completion" — Garet 30pt, y=57 ─────────────────
      // f(hasGaret ? "Garet" : "helvetica", "normal", 30, C.purple)
      // doc.text("Certificate of Completion", LX, 57)

      // // teal accent underline
      // doc.setDrawColor(...C.teal)
      // doc.setLineWidth(0.7)
      // doc.line(LX, 61, LX + 55, 61)

      // ── 3. "This certifies that" — italic ─────────────────────────────────
      f("helvetica", "italic", 9.5, C.mid)
      doc.text("This certifies that", LX, 76)

      // ── 4. Student name (+5mm gap after "This certifies that") ───────────
      const name = studentName || `${cert.student?.firstName ?? ""} ${cert.student?.lastName ?? ""}`.trim() || "Student"
      f("helvetica", "bold", 26, C.dark)
      doc.text(name, LX, 86)

      // ── 5. Teal divider ────────────────────────────────────────────────────
      doc.setDrawColor(...C.teal)
      doc.setLineWidth(0.5)
      doc.line(LX, 91, RX, 91)

      // ── 6. "has successfully completed…" ──────────────────────────────────
      f("helvetica", "normal", 9, C.mid)
      doc.text("has successfully completed the certification assessment for", LX, 98)

      // ── 7. Course name (wrap long titles) ─────────────────────────────────
      f("helvetica", "bold", 14, C.dark)
      const wrappedCourse = doc.splitTextToSize(courseName, RX - LX)
      doc.text(wrappedCourse, LX, 107)
      const courseH = wrappedCourse.length * 6.5

      // ── 8. Issue date ──────────────────────────────────────────────────────
      const dateY = 107 + courseH + 3
      f("helvetica", "normal", 7.5, C.light)
      doc.text(`Issued ${issueLabel} `, LX, dateY)

      // ── 9. Skill tags ──────────────────────────────────────────────────────
      if (skillTags.length > 0) {
        f("helvetica", "bold", 7.5, C.teal)
        doc.text(skillTags.slice(0, 6).join("  ·  "), LX, dateY + 8)
      }

      // ── 10. Score breakdown ────────────────────────────────────────────────
      const scoreY = dateY + (skillTags.length > 0 ? 18 : 10)
      const colW   = 43

      ;[
        { label: "MCQ QUIZ",       score: cert.mcqScore     },
        { label: "CODE CHALLENGE", score: cert.codeScore    },
        { label: "PROJECT REVIEW", score: cert.projectScore },
      ].forEach(({ label, score }, i) => {
        const cx = LX + i * (colW + 4)

        f("helvetica", "normal", 6, C.light)
        doc.text(label, cx, scoreY)

        f("helvetica", "bold", 13, C.dark)
        doc.text(score != null ? `${score}%` : "—", cx, scoreY + 7)

        doc.setFillColor(210, 218, 228)
        doc.roundedRect(cx, scoreY + 9, colW, 2, 0.6, 0.6, "F")
        if (score != null && score > 0) {
          doc.setFillColor(...C.teal)
          doc.roundedRect(cx, scoreY + 9, colW * (score / 100), 2, 0.6, 0.6, "F")
        }
      })

      // ── 11. Hairline rule above footer ─────────────────────────────────────
      const footY = scoreY + 20
      doc.setDrawColor(200, 210, 220)
      doc.setLineWidth(0.2)
      doc.line(LX, footY-5, RX+5, footY-5)

      // ── 12. Credential ID (monospace) ──────────────────────────────────────
      doc.setFont("courier", "normal")
      doc.setFontSize(7)
      doc.setTextColor(...C.light)
      doc.text(cert.credentialId, LX, footY)

      // ── 13. Verified Credential dot + label ────────────────────────────────
      doc.setFillColor(...C.teal)
      doc.circle(LX + 1.4, footY + 4.4, 1.2, "F")
      f("helvetica", "bold", 8, C.teal)
      doc.text("Verified Credential", LX + 5, footY + 5.6)

      // ── 14. Verify URL ─────────────────────────────────────────────────────
      f("helvetica", "normal", 5.5, C.light)
      doc.text(credUrl, LX, footY + 11)

      // ── 15. QR code — left-aligned, below verify URL ───────────────────────
      try {
        const qrSize = 22
        const qrData = await QRCode.toDataURL(credUrl, {
          width: 256, margin: 1,
          color: { dark: "#0F172A", light: "#FFFFFF00" },
        })
        doc.addImage(qrData, "PNG", LX, footY + 14, qrSize, qrSize)
        f("helvetica", "normal", 5.5, C.light)
        doc.text("Scan to verify", LX + qrSize / 2, footY + 14 + qrSize + 2.5, { align: "center" })
      } catch { /* skip if QR fails */ }

      doc.save(`Certificate-${cert.credentialId}.pdf`)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div
      className="overflow-hidden rounded-2xl border border-richblack-700 bg-richblack-800"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Top section */}
      <div className="flex gap-0">
        <div className="w-1.5 shrink-0 bg-caribbeangreen-300" />

        <div className="flex-1 px-8 py-7">
          <p className="mb-4 text-sm font-extrabold tracking-widest text-caribbeangreen-300">
            KODEVEDIC
          </p>

          <p className="text-sm text-richblack-300">This certifies that</p>
          <p className="mt-1 text-3xl font-extrabold text-richblack-5">
            {studentName || `${cert.student?.firstName ?? ""} ${cert.student?.lastName ?? ""}`.trim() || "Student"}
          </p>
          <p className="mt-2 text-sm text-richblack-300">
            has successfully completed the certification assessment for
          </p>
          <p className="mt-1 text-lg font-bold text-richblack-5">{courseName}</p>

          <p className="mt-2 text-xs text-richblack-400">
            Issued {issueLabel} &middot; 
          </p>

          {skillTags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {skillTags.map((tag, i) => (
                <span
                  key={i}
                  className="rounded-full border border-caribbeangreen-300/40 bg-caribbeangreen-300/10 px-3 py-0.5 text-xs font-medium text-caribbeangreen-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Score breakdown */}
      <div className="grid grid-cols-3 divide-x divide-richblack-700 border-t border-richblack-700">
        <ScoreCol label="MCQ quiz"       score={cert.mcqScore} />
        <ScoreCol label="Code challenge" score={cert.codeScore} />
        <ScoreCol label="Project review" score={cert.projectScore} />
      </div>

      {/* Footer */}
      <div className="flex flex-col items-start justify-between gap-4 border-t border-richblack-700 px-6 py-4 sm:flex-row sm:items-center">
        <div>
          <p className="font-mono text-xs font-semibold text-richblack-400">
            {cert.credentialId}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs font-semibold text-caribbeangreen-300">
            <span className="h-2 w-2 rounded-full bg-caribbeangreen-300" />
            Verified credential
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 rounded-xl border border-richblack-600 bg-richblack-700 px-4 py-2 text-sm font-medium text-richblack-100 transition-all hover:bg-richblack-600 disabled:opacity-60"
          >
            {downloading
              ? <><FiLoader size={14} className="animate-spin" /> Generating…</>
              : <><FiDownload size={14} /> Download PDF</>
            }
          </button>
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-xl border border-richblack-600 bg-richblack-700 px-4 py-2 text-sm font-medium text-richblack-100 transition-all hover:bg-richblack-600"
          >
            <FiExternalLink size={14} />
            Share on LinkedIn
          </a>
        </div>
      </div>
    </div>
  )
}
